/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from "react";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  AutocompleteRenderGroupParams,
  Chip,
  List,
  TextField
} from "@mui/material";
import { Instance } from "@popperjs/core";
import { calcFieldsToDisplay } from "constants/reports";
import { useCharacteristics, useSimpleMeasures } from "hooks";
import { difference, startCase } from "lodash";
import { useCalculatedSelector } from "store";
import { camelCaseToTitle } from "utils";

import { State } from "./CreateReportSection";

interface Props {
  value: Option[];
  onChange: (value: Option[]) => void;
  selectedGroups?: (keyof State["table"]["columns"])[];
}

const groupsKeyTitle: Record<keyof State["table"]["columns"], string> = {
  measures: "Measures",
  characteristics: "Characteristics",
  calcFields: "Calculated Measures"
};

type Option = { type: keyof State["table"]["columns"]; value: string };

export const GroupedColumnsPicker: React.FC<Props> = ({
  value,
  onChange,
  selectedGroups = ["measures", "characteristics", "calcFields"]
}) => {
  const characteristicsAll = useCharacteristics();
  const calculatedFieldsAll = useCalculatedSelector();
  const simpleMeasures = useSimpleMeasures();

  const uniqueCalFieldsToShow = difference(calculatedFieldsAll, calcFieldsToDisplay);

  const popperRef = useRef<Instance>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const options = useMemo<Option[]>(() => {
    const result: Option[] = [];
    if (simpleMeasures.length && selectedGroups?.includes("measures")) {
      simpleMeasures.forEach((item) => {
        result.push({ type: "measures", value: String(item) });
      });
    }
    if (characteristicsAll.length && selectedGroups?.includes("characteristics")) {
      characteristicsAll.forEach((item) => {
        result.push({ type: "characteristics", value: String(item) });
      });
    }
    if (uniqueCalFieldsToShow.length && selectedGroups?.includes("calcFields")) {
      uniqueCalFieldsToShow.forEach((item) => {
        result.push({ type: "calcFields", value: String(item) });
      });
    }
    return result;
  }, [characteristicsAll, uniqueCalFieldsToShow, simpleMeasures, selectedGroups]);

  return (
    <Autocomplete
      options={options}
      renderInput={(params) => <TextField {...params} label="Parameters" />}
      renderOption={(props, option) => <li {...props}>{startCase(option.value as string)}</li>}
      getOptionLabel={(option) => camelCaseToTitle(option.value)}
      renderTags={(tagValue, getTagProps) => {
        return (
          <div
            style={{
              maxHeight: "85px",
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexWrap: "wrap",
              gap: "4px"
            }}
          >
            {tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} label={startCase(option.value as string)} {...tagProps} />;
            })}
          </div>
        );
      }}
      slotProps={{ popper: { popperRef } }}
      groupBy={(option) => option.type}
      value={value}
      onChange={(_, value) => {
        onChange(value);
      }}
      isOptionEqualToValue={(option, value) => option.type === value.type && option.value === value.value}
      renderGroup={(params) => (
        <li key={params.key}>
          <Group params={params} popperRef={popperRef} searchValue={inputRef.current?.value || ""} />
        </li>
      )}
      multiple
      disableCloseOnSelect
    />
  );
};

interface GroupProps {
  params: AutocompleteRenderGroupParams;
  popperRef: React.RefObject<any>;
  searchValue: string;
}

const Group: React.FC<GroupProps> = ({ params, popperRef, searchValue }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (searchValue.length >= 2) {
      setIsExpanded(true);
    }
  }, [searchValue]);

  return (
    <Accordion
      expanded={isExpanded}
      sx={{ borderRadius: "0 !important", color: "#0a3142", boxShadow: "none" }}
      onChange={(_, expanded) => {
        setIsExpanded(!isExpanded);
        if (expanded) {
          setTimeout(() => popperRef.current.update(), 300);
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ArrowForwardIosIcon sx={{ fontSize: "0.9rem", fill: "#5D7782" }} />}
        sx={{
          fontWeight: 600,
          fontSize: 15,
          flexDirection: "row-reverse",
          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
            transform: "rotate(90deg)"
          },
          "& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded": {
            margin: "0 0 0 16px"
          },
          "&.Mui-expanded": {
            minHeight: 48
          }
        }}
      >
        {groupsKeyTitle[params.group as keyof typeof groupsKeyTitle]}
      </AccordionSummary>
      <AccordionDetails sx={{ paddingTop: 0, paddingBottom: 0 }}>
        <List sx={{ padding: 0, "& li": { fontSize: 16, marginLeft: "16px" } }}>{params.children}</List>
      </AccordionDetails>
    </Accordion>
  );
};
